/**
 * Stage 2c: Competitor Extract — context-based version using StagePanel.
 *
 * Fan-out stage: extracts and analyzes content from each competitor URL.
 * Shows a card list of competitors with per-item and batch run controls.
 */

import React from 'react';
import { StagePanel } from '../StagePanel';
import { useArticleContext } from '../ArticleContext';
import { useBatchExecution } from '../shared/useBatchExecution';
import type { ArticleDocument } from '../types';

function CompetitorCards() {
  const { document } = useArticleContext();
  const urls = document?.targeting?.competitors?.urls || [];
  const extractions = document?.targeting?.competitors?.extractions || [];
  const {
    selectedId, setSelectedId,
    runningAll, batchStatus, runningSingle,
    runSingle, runAll, cancelBatch,
  } = useBatchExecution({ stageId: 'stage_2c_competitor_extract' });

  // Map extractions back to URLs by domain for status display
  const extractedDomains = new Set(extractions.map((e) => e.domain));

  const selectedExtraction = extractions.find((e) => e.domain === selectedId || e.url === selectedId);

  return (
    <div className="space-y-3">
      {/* Batch controls */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="px-3 py-1.5 text-sm rounded-md text-white hover:opacity-90 disabled:opacity-50" style={{ backgroundColor: '#1a2332' }}
          disabled={runningAll || urls.length === 0}
          onClick={() => runAll(urls.map(u => u.id || u.domain))}
        >
          {runningAll ? 'Running All...' : `Run All (${urls.length})`}
        </button>
        {runningAll && (
          <button
            type="button"
            className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
            onClick={cancelBatch}
          >
            Cancel
          </button>
        )}
      </div>

      {/* Competitor cards */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {urls.map((entry) => {
          const id = entry.id || entry.domain;
          const isExtracted = extractedDomains.has(entry.domain);
          const batch = batchStatus[id];
          return (
            <button
              key={id}
              type="button"
              className={`flex-shrink-0 border rounded px-3 py-2 text-left text-xs w-40 ${
                selectedId === id ? 'ring-2 ring-blue-500 border-blue-300' : 'border-gray-200'
              } ${isExtracted ? 'bg-green-50' : 'bg-white'}`}
              onClick={() => setSelectedId(id)}
            >
              <div className="font-medium text-gray-800 truncate">{entry.domain}</div>
              <div className="text-gray-400">#{entry.position}</div>
              {batch && (
                <div className={`text-[10px] mt-1 ${batch.status === 'succeeded' ? 'text-green-600' : batch.status === 'failed' ? 'text-red-600' : 'text-blue-600'}`}>
                  {batch.status}
                </div>
              )}
              {!batch && isExtracted && (
                <div className="text-[10px] mt-1 text-green-600">extracted</div>
              )}
            </button>
          );
        })}
      </div>

      {/* Selected extraction detail */}
      {selectedId && (
        <div className="border rounded p-3 bg-white">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-medium text-gray-800">{selectedId}</div>
            <button
              type="button"
              className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              disabled={runningSingle || runningAll}
              onClick={() => runSingle(selectedId)}
            >
              {runningSingle ? 'Running...' : 'Extract'}
            </button>
          </div>
          {selectedExtraction ? (
            <div className="text-xs text-gray-600 space-y-1">
              {selectedExtraction.page_title && <div><span className="text-gray-400">Title:</span> {selectedExtraction.page_title}</div>}
              {selectedExtraction.approximate_word_count && <div><span className="text-gray-400">Words:</span> ~{selectedExtraction.approximate_word_count}</div>}
              {selectedExtraction.topics_covered && (
                <div><span className="text-gray-400">Topics:</span> {selectedExtraction.topics_covered.join(', ')}</div>
              )}
            </div>
          ) : (
            <div className="text-xs text-gray-400">Not yet extracted.</div>
          )}
        </div>
      )}
    </div>
  );
}

function renderOutput(doc: ArticleDocument) {
  const extractions = doc.targeting?.competitors?.extractions || [];
  if (extractions.length === 0) {
    return <div className="text-sm text-gray-500">No extractions yet.</div>;
  }
  return (
    <div>
      <div className="text-xs font-medium text-gray-700 mb-1">
        Extracted {extractions.length} competitors
      </div>
      <div className="space-y-1">
        {extractions.map((e, i) => (
          <div key={i} className="text-xs border rounded px-2 py-1 border-gray-200">
            <span className="font-medium">{e.domain}</span> — {e.page_title || 'No title'}
            <span className="text-gray-400 ml-2">~{e.approximate_word_count || '?'} words</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function Stage2cCompetitorExtractV2() {
  const { document } = useArticleContext();
  const hasUrls = (document?.targeting?.competitors?.urls || []).length > 0;

  return (
    <StagePanel
      stageId="stage_2c_competitor_extract"
      title="Stage 2c: Competitor Extract"
      description="Fetches and analyzes content from each competitor URL."
      hideRunButton
      renderOutput={renderOutput}
      runDisabled={!hasUrls}
    >
      {hasUrls ? <CompetitorCards /> : (
        <div className="text-xs text-gray-500">No competitor URLs. Run Stage 2b first.</div>
      )}
    </StagePanel>
  );
}
